/**
 * Route → guide registry.
 *
 * UI-agnostic logic. EDIT points:
 *   - the imports and the `HELP_REGISTRY` entries — one per page that has a guide.
 */
import type { PageHelp } from './types';
import { bankStatementsHelp } from '../pages/BankStatements/help';
import { insurerReportsHelp } from '../pages/InsurerReports/help';
import { paymentPlansHelp } from '../pages/PaymentPlan/help';
import { clientBalanceHelp } from '../pages/ClientBalance/help';
import { paymentsHelp } from '../pages/Payments/help';
import { remindersHelp } from '../pages/Reminders/help';

/**
 * Route → page guide. A route that is not listed here simply has no "?" button, which is
 * what lets the manual roll out page by page instead of all at once.
 *
 * A key may carry `:param` segments, matching the app's own route patterns — a detail page
 * is a different page with a different guide, not a longer version of its list.
 *
 * Keep this file free of React/UI imports: `scripts/help-check.mjs` and the browser audit
 * spec import it directly, and a UI import would break both.
 */
export const HELP_REGISTRY: Record<string, PageHelp> = {
  '/bankStatements': bankStatementsHelp,
  '/insurerReports': insurerReportsHelp,
  '/paymentPlans': paymentPlansHelp,
  '/clientBalance': clientBalanceHelp,
  '/payments': paymentsHelp,
  '/reminders': remindersHelp,
};

const isPattern = (route: string): boolean => route.includes('/:');

/**
 * Segment-wise match, where `:param` accepts any single non-empty segment.
 *
 * Exported because the Ask panel asks the same question of an entry's `anchor_route`:
 * "is the control this answer describes on the screen the user is standing on?" A literal route
 * matches by equality, so one helper covers both kinds.
 */
export const matchesRoute = (route: string, pathname: string): boolean => {
  const pattern = route.split('/');
  const actual = pathname.split('/');
  if (pattern.length !== actual.length) return false;
  return pattern.every((segment, i) =>
    segment.startsWith(':') ? (actual[i] ?? '').length > 0 : segment === actual[i],
  );
};

export const helpForPath = (pathname: string): PageHelp | null => {
  const exact = HELP_REGISTRY[pathname];
  if (exact) return exact;

  // Patterns before prefixes: /orders/42 belongs to the detail guide, not to the list
  // guide it happens to sit under. Fewest params wins, so a literal-heavy route is preferred.
  const pattern = Object.keys(HELP_REGISTRY)
    .filter((route) => isPattern(route) && matchesRoute(route, pathname))
    .sort((a, b) => a.split('/:').length - b.split('/:').length)[0];
  if (pattern) return HELP_REGISTRY[pattern] ?? null;

  // Prefix match so a detail route with no guide of its own still gets its list's guide.
  // Longest match wins, so /a/b never shadows /a/b/c.
  const hit = Object.keys(HELP_REGISTRY)
    .filter((route) => !isPattern(route) && pathname.startsWith(`${route}/`))
    .sort((a, b) => b.length - a.length)[0];
  return hit ? (HELP_REGISTRY[hit] ?? null) : null;
};
